import { useCallback, useMemo } from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Image } from '@/atoms/Image'
import { AsH2, CaptionMD, HeadingLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { DocumentCard } from '@/molecules/DocumentCard'
import { EmbeddedVideo as Video } from '@/molecules/EmbeddedVideo'
import { ExhibitorsHeader } from '@/organisms/ExhibitorsHeader'
import { options } from '@/services/ContentfulService'
import { Project } from '@/services/PressService'
import { EmbeddedVideo } from '@/types/codegen-contentful'
import { buildCloudinaryImagePathFromContentfulObject, getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface Download {
  title?: string | null
  subtitle?: string | null
  href?: string | null
  image?: string | null
  type?: string | null
  location?: string | null
  sys: {
    id: string
  }
}

interface ExhibitorsViewProps {
  assets: Download[]
  keyArt: Download[]
  keyImages: Download[]
  project?: Project
  trailers: Download[]
  videos: EmbeddedVideo[]
}

export const ExhibitorsView: React.FC<ExhibitorsViewProps> = ({
  assets,
  keyArt,
  keyImages,
  project,
  trailers,
  videos,
}) => {
  const { t } = useTranslate('partnership')
  const track = useSafeTrack()
  const { locale } = useLocale()
  const downloadImage = useMemo(() => {
    return locale === 'es'
      ? getCloudinaryImageUrl({
          path: 'v1710272728/angel-app/Cabrini/Screenshot_2024-03-12_at_1.44.48_PM.png',
        })
      : getCloudinaryImageUrl({
          path: 'v1705002810/angel-app/Cabrini/Screenshot_2024-01-11_at_12.50.21_PM.png',
        })
  }, [locale])

  const logo = project?.logo?.[0]
    ? buildCloudinaryImagePathFromContentfulObject(project?.logo?.[0])
    : 'v1695393762/angel-studios/logos/Angel-Studios-Logo-Copper.svg'

  const handleDownloadableClick = useCallback(
    (downloadable: Download) => {
      track('Exhibitor Downloadable Clicked', {
        project_slug: project?.slug ?? slugs.angelStudios,
        title: downloadable.title,
        type: downloadable.type,
      })
    },
    [project, track],
  )

  return (
    <div className="bg-white pt-16">
      <ExhibitorsHeader logo={logo} />
      <main className="md:w-7-12 mx-auto flex w-11/12 max-w-5xl flex-col gap-10 py-10">
        {project?.exhibitorCustomInfoSection?.json && (
          <section id="about">{documentToReactComponents(project?.exhibitorCustomInfoSection?.json, options)}</section>
        )}
        {project && trailers?.length > 0 && (
          <div className="flex flex-col gap-10" id="media-kits">
            <HeadingLG as={AsH2} weight="bold">
              {t('trailers', 'Trailers')}
            </HeadingLG>
            <CaptionMD>
              <Translate i18nKey="clickImagesDownload" t={t}>
                Click on the images below and then hit{' '}
                <Image src={downloadImage} alt="Download Button" className="inline" height={15} width={110} /> in the
                top right-hand corner.
              </Translate>
            </CaptionMD>
            <div className="flex flex-wrap gap-2">
              {trailers.map((downloadable) => {
                return downloadable.image && downloadable.href ? (
                  <DocumentCard
                    authenticated={true}
                    href={downloadable.href}
                    image={downloadable.image}
                    key={downloadable.sys.id}
                    onClick={() => handleDownloadableClick(downloadable)}
                    returnTo={paths.press.index}
                    title={downloadable.title}
                    type={downloadable.subtitle}
                    variant="light"
                  />
                ) : null
              })}
            </div>
          </div>
        )}
        {project && Boolean(videos?.length) && (
          <div className="flex flex-col gap-10" id="trailers">
            <Translate i18nKey="officialVideos" t={t}>
              <HeadingLG as={AsH2} weight="bold">
                Official Videos
              </HeadingLG>
            </Translate>
            <div className="flex flex-wrap gap-2">
              {videos.map((video) => {
                return video.url ? <Video className="w-full" title={video.title as string} url={video.url} /> : null
              })}
            </div>
          </div>
        )}
        {project && keyArt?.length > 0 && (
          <div className="flex flex-col gap-10" id="media-kits">
            <HeadingLG as={AsH2} weight="bold">
              {t('keyArt', 'Key Art')}
            </HeadingLG>
            <CaptionMD>
              <Translate i18nKey="clickImagesDownload" t={t}>
                Click on the images below and then hit{' '}
                <Image src={downloadImage} alt="Download Button" className="inline" height={15} width={110} /> in the
                top right-hand corner.
              </Translate>
            </CaptionMD>
            <div className="flex flex-wrap gap-2">
              {keyArt.map((downloadable) => {
                return downloadable.image && downloadable.href ? (
                  <DocumentCard
                    authenticated={true}
                    href={downloadable.href}
                    image={downloadable.image}
                    key={downloadable.sys.id}
                    onClick={() => handleDownloadableClick(downloadable)}
                    returnTo={paths.press.index}
                    title={downloadable.title}
                    type={downloadable.subtitle}
                    variant="light"
                  />
                ) : null
              })}
            </div>
          </div>
        )}
        {project && assets?.length > 0 && (
          <div className="flex flex-col gap-10" id="media-kits">
            <HeadingLG as={AsH2} weight="bold">
              {t('assets', 'Assets')}
            </HeadingLG>
            <CaptionMD>
              <Translate i18nKey="clickImagesDownload" t={t}>
                Click on the images below and then hit{' '}
                <Image src={downloadImage} alt="Download Button" className="inline" height={15} width={110} /> in the
                top right-hand corner.
              </Translate>
            </CaptionMD>
            <div className="flex flex-wrap gap-2">
              {assets.map((downloadable) => {
                return downloadable.image && downloadable.href ? (
                  <DocumentCard
                    authenticated={true}
                    href={downloadable.href}
                    image={downloadable.image}
                    key={downloadable.sys.id}
                    onClick={() => handleDownloadableClick(downloadable)}
                    returnTo={paths.press.index}
                    title={downloadable.title}
                    type={downloadable.subtitle}
                    variant="light"
                  />
                ) : null
              })}
            </div>
          </div>
        )}
        {project && keyImages?.length > 0 && (
          <div className="flex flex-col gap-10" id="media-kits">
            <HeadingLG as={AsH2} weight="bold">
              {t('keyImages', 'Key Images')}
            </HeadingLG>
            <CaptionMD>
              <Translate i18nKey="clickImagesDownload" t={t}>
                Click on the images below and then hit{' '}
                <Image src={downloadImage} alt="Download Button" className="inline" height={15} width={110} /> in the
                top right-hand corner.
              </Translate>
            </CaptionMD>
            <div className="flex flex-wrap gap-2">
              {keyImages.map((downloadable) => {
                return downloadable.image && downloadable.href ? (
                  <DocumentCard
                    authenticated={true}
                    href={downloadable.href}
                    image={downloadable.image}
                    key={downloadable.sys.id}
                    onClick={() => handleDownloadableClick(downloadable)}
                    returnTo={paths.press.index}
                    title={downloadable.title}
                    type={downloadable.subtitle}
                    variant="light"
                  />
                ) : null
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
